import { CloseButton, Table } from 'lib/components';
import { Option } from 'lib/components/dropdown/Dropdown';
import {
  Content,
  ContentBody,
  ContentHeader,
  ContentHeaderWrap,
  ErrorMessage,
  Label,
  ModalItem,
  Row,
  SelectInput,
  TableCell,
  TableCellHeader,
  Text,
} from 'lib/components/modal/importModal/Components';
import {
  colOptionProps,
  csvDataProps,
} from 'lib/components/modal/importModal/Types';
import { theme } from 'lib/style';
import * as React from 'react';
import { Button } from 'react-covideo-common';
import { MdWarning } from 'react-icons/md';
import {
  AUTOMOTIVE_ROLE_LABEL,
  AUTOMOTIVE_ROLE_OPTION,
  FIELD_REQUIRED_TEXT,
} from './Constants';
import { every, values } from 'lodash';
import { Flex } from 'lib/components/styles/layout';
import coloredInfoIcon from 'assets/icons/coloredInfoIcon.png';
import { useTheme } from 'styled-components/macro';
import { CSSObjectWithLabel } from 'react-select';

export const initialColOptions: colOptionProps[] = [];
export const SAMPLE_LENGTH = 1;

const getFieldOptions = (
  hasEmail2FieldAccess: boolean,
  isAutomotive: boolean
) => {
  const fieldOptions = [
    { value: 'None', label: 'Choose...' },
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'username', label: 'Username' },
    { value: 'phone1', label: 'Phone Number 1' },
    { value: 'phone2', label: 'Phone Number 2' },
    { value: 'phone1Extension', label: 'Phone 1 Extension' },
    { value: 'phone2Extension', label: 'Phone 2 Extension' },
    { value: 'cellPhone', label: 'Cell Phone Number' },
    { value: 'email', label: 'Email' },
    { value: 'password', label: 'Password' },
    // { value: 'phone2', label: 'Phone Number-2' },
    { value: 'title', label: 'Title' },
    { value: 'departmentName', label: 'Department' },
    { value: 'isAdmin', label: 'Admin' },
    { value: 'nmls', label: 'NMLS' },
    { value: 'userType', label: 'User Type' },
    // Super admin user fields
    { value: 'active', label: 'Active' },
    { value: 'transcriptionAccess', label: 'Enable Captions' },
    { value: 'mobileSMSAccess', label: 'Mobile SMS' },
  ];
  if (hasEmail2FieldAccess) {
    fieldOptions.push({ value: 'email2', label: 'Email2' });
  }
  if (isAutomotive) {
    fieldOptions.push(AUTOMOTIVE_ROLE_OPTION);
  }
  return fieldOptions;
};

const getRequiredFields = (
  isAutomotive: boolean,
  fieldOptions: {
    value: string;
    label: string;
  }[],
  isEmail2Mandatory: boolean
) => {
  const requiredFields = ['firstName', 'lastName', 'email'];
  if (isAutomotive && fieldOptions.includes(AUTOMOTIVE_ROLE_OPTION)) {
    requiredFields.push(AUTOMOTIVE_ROLE_LABEL);
  }
  if (isEmail2Mandatory) {
    requiredFields.push('email2');
  }
  return requiredFields;
};

const getFieldsForValidation = () => {
  return ['phone1', 'phone2', 'cellPhone'];
};

type Props = {
  title: string;
  disabled: boolean;
  colOptions: colOptionProps[];
  colSamples: { [key: string]: string[] };
  colMapping: { [key: string]: string };
  invalidCount: number;
  CSVData: csvDataProps;
  duplicatedRows: { [key: string]: string[] };
  onColChange(field: string, value: string): void;
  initializeListView(): void;
  handleModalClose(): void;
  hasEmail2FieldAccess?: boolean;
  isAutomotive: boolean;
  errors: {
    firstName: string;
    lastName: string;
    email: string;
    isAdmin: string;
    automotiveRoleLabel: string;
  };
  isEmail2Mandatory: boolean;
};

export const MapImported = ({
  title,
  disabled,
  colOptions,
  colSamples,
  colMapping,
  invalidCount,
  CSVData,
  duplicatedRows,
  onColChange,
  handleModalClose,
  initializeListView,
  hasEmail2FieldAccess = false,
  isAutomotive,
  errors,
  isEmail2Mandatory,
}: Props) => {
  const fieldOptions = getFieldOptions(hasEmail2FieldAccess, isAutomotive);
  const requiredFields = getRequiredFields(
    isAutomotive,
    fieldOptions,
    isEmail2Mandatory
  );
  const validationFields = getFieldsForValidation();
  const getSampleData = (index: string) => {
    if (!colSamples[index]) {
      return '';
    }
    return colSamples[index].filter(Boolean).slice(0, SAMPLE_LENGTH);
  };
  const themes = useTheme();
  const printDuplicatedRows = React.useCallback(() => {
    const result: React.ReactElement[] = [];
    for (let username in duplicatedRows) {
      if (duplicatedRows[username].length > 1) {
        result.push(
          <Label key={`error-key-${username}`}>
            <Text>
              {' '}
              {username || '""'} has multiple entries( line{' '}
              {duplicatedRows[username].join(', ')})
            </Text>
          </Label>
        );
      }
    }
    return result;
  }, [duplicatedRows]);

  return (
    <ModalItem gap={'12px'}>
      <Content style={{ width: 'auto' }}>
        <ContentHeaderWrap>
          <ContentHeader>{title}</ContentHeader>
          <CloseButton disabled={disabled} onClick={handleModalClose} />
        </ContentHeaderWrap>
        <ContentBody>
          <div style={{ marginBottom: 42 }}>
            Connect columns from .csv file to user fields
          </div>
          <Table
            compact={true}
            headers={[
              <TableCellHeader>Column Label (csv)</TableCellHeader>,
              <TableCellHeader>Example</TableCellHeader>,
              <TableCellHeader>User Field</TableCellHeader>,
            ]}
            hoverable={false}
            rows={colOptions.slice(1).map((x, index: number) => ({
              key: index,
              columns: [
                <TableCell>{x.label}</TableCell>,
                <TableCell>{getSampleData(index.toString())}</TableCell>,
                <TableCell>
                  <SelectInput
                    menuPlacement='auto'
                    menuPosition='fixed'
                    placeholder='Choose...'
                    styles={{
                      control: (base: CSSObjectWithLabel) => ({
                        ...base,
                        height: '40px',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                    }}
                    options={fieldOptions}
                    value={fieldOptions.find(
                      option =>
                        option.value ==
                        (Object.keys(colMapping).find(
                          key => colMapping[key] === index.toString()
                        ) || 'None')
                    )}
                    onChange={(newValue: unknown) => {
                      const option = newValue as Option;
                      onColChange(option.value.toString(), index.toString());
                    }}
                  />
                </TableCell>,
              ],
              onClick: () => {},
            }))}
          />
          {Object.values(errors).every(key => key === '') &&
            invalidCount > 0 &&
            invalidCount < CSVData.data.length && (
              <>
                <Row>
                  <ErrorMessage>
                    <span>
                      <MdWarning
                        color={theme.palette.primaryRedDanger}
                        size={32}
                      />
                      {invalidCount}/{CSVData.data.length} rows contain invalid
                      data. Those rows will be ignored.
                    </span>
                  </ErrorMessage>
                </Row>
                <div style={{ maxHeight: 84, overflowY: 'auto' }}>
                  {printDuplicatedRows()}
                </div>
              </>
            )}
          {invalidCount == CSVData.data.length && (
            <>
              <div style={{ maxHeight: 84, overflowY: 'auto' }}>
                {printDuplicatedRows()}
              </div>
            </>
          )}

          {Object.keys(errors).some(
            key =>
              validationFields.includes(key) &&
              // @ts-ignore
              errors[key] !== ''
          ) && (
            <>
              <Row>
                <ErrorMessage style={{ minHeight: '48px', height: 'auto' }}>
                  <span
                    style={{
                      margin: '10px',
                      alignItems: 'center',
                      lineHeight: 'normal',
                    }}
                  >
                    <div>
                      <MdWarning
                        color={theme.palette.primaryRedDanger}
                        size={24}
                        style={{ marginTop: '0px', marginRight: 10 }}
                      />
                    </div>
                    <Flex>
                      {Object.keys(errors)
                        // @ts-ignore
                        .map((key: string) =>
                          validationFields.includes(key) &&
                          // @ts-ignore
                          errors[key] !== '' ? (
                            // @ts-ignore
                            <div key={key}>{errors[key]}</div>
                          ) : null
                        )}
                    </Flex>
                  </span>
                </ErrorMessage>
              </Row>
            </>
          )}

          {Object.keys(errors).some(
            key =>
              requiredFields.includes(key) &&
              // @ts-ignore
              errors[key] !== ''
          ) && (
            <>
              <Row>
                <ErrorMessage style={{ minHeight: '48px', height: 'auto' }}>
                  <span
                    style={{
                      margin: '10px',
                      alignItems: 'center',
                      lineHeight: 'normal',
                    }}
                  >
                    <div>
                      <MdWarning
                        color={theme.palette.primaryRedDanger}
                        size={24}
                        style={{ marginTop: '0px', marginRight: 10 }}
                      />
                    </div>
                    {Object.keys(errors)
                      .filter(
                        key =>
                          requiredFields.includes(key) &&
                          // @ts-ignore
                          errors[key] !== ''
                      )
                      .join(', ') +
                      FIELD_REQUIRED_TEXT +
                      Object.keys(errors)
                        // @ts-ignore
                        .filter(
                          key =>
                            requiredFields.includes(key) &&
                            // @ts-ignore

                            errors[key] !== ''
                        )
                        .join(', ')}{' '}
                  </span>
                </ErrorMessage>
              </Row>
            </>
          )}
        </ContentBody>
      </Content>
      <Flex
        flexDirection='row'
        alignItems='flex-start'
        justifyContent='space-between'
        gap='12px'
      >
        <Flex
          flexDirection='row'
          alignItems='center'
          gap='8px'
          color={themes.colors.neutral['60']}
          maxWidth='430px'
          minWidth='430px'
          height='40px'
        >
          <img src={coloredInfoIcon} alt='info' width={20} height={20} />
          <Label style={{ fontSize: '13px' }}>
            The users being uploaded are getting access to folders depending on
            their permissions
          </Label>
        </Flex>

        <Flex justifyContent='flex-end'>
          <Button
            disabled={
              CSVData.data.length - invalidCount == 0 ||
              !every(values(errors), value => value === '')
            }
            text={'Continue'}
            onClick={() => {
              initializeListView();
            }}
          />
        </Flex>
      </Flex>
    </ModalItem>
  );
};
